import { ReactNode, useCallback, useEffect, useState } from "react";
import { Button, ButtonProps } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  BackgroundProcessObjectType,
  loadBackgroundProcessSummaryForObject,
} from "./backgroundProcessingApi";
import {
  BackgroundProcessingQuestionDialog,
  BackgroundProcessingQuestionDialogContent,
} from "./BackgroundProcessingQuestionDialog";
import { BackgroundProcessSummaryDialog } from "./BackgroundProcessSummaryPanel";
import { clearCompletionHandled, markCompletionHandled, markPersistent, selectHandledCompletion, upsertProcess } from "./backgroundProcessingSlice";
import { useBackgroundProcess } from "./useBackgroundProcess";
import { isFinalStatus, isSuccessfulFinalStatus } from "./backgroundProcessStatus";

interface Props {
  triggerLabel: ReactNode;
  triggerButtonProps?: Omit<ButtonProps, "children" | "onClick">;
  triggerDisabled?: boolean;
  questionTitle: ReactNode;
  questionDescription?: ReactNode;
  questionChildren?: BackgroundProcessingQuestionDialogContent;
  questionMaxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  questionAllowFullScreen?: boolean;
  loading?: boolean;
  trackingLabel?: string;
  confirmLabel?: ReactNode;
  confirmLabelId?: string;
  confirmColor?: ButtonProps["color"];
  confirmVariant?: ButtonProps["variant"];
  confirmStartIcon?: ReactNode;
  confirmDisabled?: boolean;
  objectType: BackgroundProcessObjectType;
  objectId: string;
  onStart: () => Promise<boolean>;
  onCompleted?: () => void | Promise<void>;
  onOpenQuestion?: () => void;
  onCloseQuestion?: () => void;
}

export const BackgroundProcessLauncher = ({
  triggerLabel,
  triggerButtonProps,
  triggerDisabled = false,
  questionTitle,
  questionDescription,
  questionChildren,
  questionMaxWidth,
  questionAllowFullScreen = false,
  loading = false,
  trackingLabel,
  confirmLabel,
  confirmLabelId,
  confirmColor,
  confirmVariant,
  confirmStartIcon,
  confirmDisabled,
  objectType,
  objectId,
  onStart,
  onCompleted,
  onOpenQuestion,
  onCloseQuestion,
}: Props) => {
  const dispatch = useAppDispatch();
  const [questionOpen, setQuestionOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [checkingExistingProcess, setCheckingExistingProcess] = useState(false);

  // Polling is owned by backgroundPollerMiddleware; here we just subscribe and
  // read the latest status from the store.
  const { process, status, isFinal } = useBackgroundProcess(objectType, objectId);

  // The "completion handled" flag lives in the store, keyed by this run's token,
  // so a finished run can't re-trigger its refresh if the launcher remounts.
  const handledToken = useAppSelector((s) => selectHandledCompletion(s, objectType, objectId));
  const currentToken = process?.processNumber ?? "final";

  // The completion toast (and persistent-subscription release) is owned by
  // backgroundCompletionListener, so it fires regardless of where the user is.
  // Here we only run the on-screen refresh the caller provided, on success.
  const handleProcessCompleted = useCallback(async (finalStatus: string) => {
    if (handledToken === currentToken) {
      return;
    }

    dispatch(markCompletionHandled({ objectType, objectId, token: currentToken }));

    if (!isSuccessfulFinalStatus(finalStatus)) {
      return;
    }

    try {
      await onCompleted?.();
    } catch {
      // The caller owns its own error UX.
    }
  }, [handledToken, currentToken, dispatch, objectType, objectId, onCompleted]);

  // React to the poller marking this process final (works whether or not the
  // summary dialog is open). The summary dialog's onCompleted funnels into the
  // same guarded handler, so completion fires exactly once per run.
  useEffect(() => {
    if (isFinal && status && handledToken !== currentToken) {
      void handleProcessCompleted(status);
    }
  }, [isFinal, status, handledToken, currentToken, handleProcessCompleted]);

  const handleOpenQuestion = async () => {
    onOpenQuestion?.();
    setCheckingExistingProcess(true);

    try {
      const result = await dispatch(
        loadBackgroundProcessSummaryForObject({ objectType, objectId })
      );

      if (loadBackgroundProcessSummaryForObject.fulfilled.match(result)) {
        if (!isFinalStatus(result.payload.status)) {
          dispatch(clearCompletionHandled({ objectType, objectId }));
          dispatch(upsertProcess({
            objectType,
            objectId,
            label: trackingLabel ?? `${objectType} #${objectId}`,
            operationType: result.payload.operationType,
            processNumber: result.payload.processNumber,
            status: result.payload.status,
          }));
          dispatch(markPersistent({ objectType, objectId }));
          setSummaryOpen(true);
          return;
        }
      }
    } catch {
      // If the process check fails, fall back to the confirmation dialog.
    } finally {
      setCheckingExistingProcess(false);
    }

    setQuestionOpen(true);
  };

  const handleCloseQuestion = () => {
    onCloseQuestion?.();
    setQuestionOpen(false);
  };

  const handleConfirm = async () => {
    try {
      const started = await onStart();

      if (!started) {
        return;
      }

      dispatch(clearCompletionHandled({ objectType, objectId }));
      dispatch(upsertProcess({
        objectType,
        objectId,
        label: trackingLabel ?? `${objectType} #${objectId}`,
        status: "running",
      }));
      dispatch(markPersistent({ objectType, objectId }));
      setQuestionOpen(false);
      setSummaryOpen(true);
    } catch {
      // The caller owns user-facing error state.
    }
  };

  return (
    <>
      <Button
        {...triggerButtonProps}
        disabled={triggerDisabled || checkingExistingProcess}
        onClick={() => void handleOpenQuestion()}
      >
        {triggerLabel}
      </Button>
      <BackgroundProcessingQuestionDialog
        open={questionOpen}
        onClose={handleCloseQuestion}
        onConfirm={handleConfirm}
        loading={loading || checkingExistingProcess}
        maxWidth={questionMaxWidth}
        allowFullScreen={questionAllowFullScreen}
        title={questionTitle}
        description={questionDescription}
        confirmLabel={confirmLabel}
        confirmLabelId={confirmLabelId}
        confirmColor={confirmColor}
        confirmVariant={confirmVariant}
        confirmStartIcon={confirmStartIcon}
        confirmDisabled={confirmDisabled}
      >
        {questionChildren}
      </BackgroundProcessingQuestionDialog>
      <BackgroundProcessSummaryDialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        objectType={objectType}
        objectId={objectId}
        onCompleted={handleProcessCompleted}
      />
    </>
  );
};